import Stats from "../../components/Atomos/Icons/Stats";
import Image from "../../components/Atomos/Icons/Image";
import Log from "../../components/Atomos/Icons/Log";
import { TableActionTooltip } from "../../components/Organismos/Table/HeaderLabelWithTooltip";
import { ReactComponent as FirmDescriptionIcon } from "../../assets/images&icons/firm-description.svg";
import type { FirmLogOfficeScope } from "./Logs/firmLogsUtils";
import { MANAGE_FIRM_EXPAND_JURISDICTIONS_BUTTON_CLASS } from "./manageFirmJurisdictionsExpandButton";
import ManageFirmJurisdictionsExpandGlyph from "./ManageFirmJurisdictionsExpandGlyph";

const ACTION_ICON_BUTTON_CLASS =
  "inline-flex h-7 w-7 items-center justify-center rounded text-Default hover:bg-neutral-100";
const ACTION_ICON_SIZE_CLASS = "inline-flex h-6 w-6 shrink-0 [&_svg]:h-6 [&_svg]:w-6";
const ACTION_SVG_PATH_FILL_CLASS = "[&_path]:fill-Default";
const ACTION_SVG_ICON_CLASS = `h-6 w-6 shrink-0 ${ACTION_SVG_PATH_FILL_CLASS}`;
export type ManageFirmOfficeRowActionHandlers = {
  onOpenStatistics?: (input: {
    officeId: string;
    firmId: string;
    label: string;
  }) => void;
  onOpenFirmDescription?: (input: { firmId: string; name: string }) => void;
  onOpenFirmLogo?: (input: { firmId: string; name: string }) => void;
  onOpenFirmLog?: (input: {
    firmId: string;
    name: string;
    officeScope?: FirmLogOfficeScope;
  }) => void;
  onToggleFirmExpanded?: (firmId: string) => void;
};

type ManageFirmOfficeRowActionsProps = {
  officeId?: string;
  firmId?: string;
  firmName?: string;
  jurisdiction?: string;
  showFirmActions?: boolean;
  showOfficeLog?: boolean;
  officeScope?: FirmLogOfficeScope;
  expandFirmId?: string;
  isExpanded?: boolean;
  handlers: ManageFirmOfficeRowActionHandlers;
};

export default function ManageFirmOfficeRowActions ({
  officeId = "",
  firmId = "",
  firmName = "",
  jurisdiction = "",
  showFirmActions = false,
  showOfficeLog = false,
  officeScope,
  expandFirmId = "",
  isExpanded = false,
  handlers,
}: ManageFirmOfficeRowActionsProps): JSX.Element | null {
  const hasExpand = Boolean(expandFirmId && handlers.onToggleFirmExpanded);
  const showLogButton =
    Boolean(handlers.onOpenFirmLog) &&
    firmId &&
    (showFirmActions || showOfficeLog);
  if (!officeId && !hasExpand && !showLogButton) {
    return null;
  }

  const statsLabel = jurisdiction
    ? `${firmName} — ${jurisdiction}`
    : firmName;

  const openFirmLog = () => {
    handlers.onOpenFirmLog?.({
      firmId,
      name: firmName,
      officeScope,
    });
  };

  return (
    <div className="inline-flex shrink-0 items-center gap-1 w-full">
      {officeId && firmId && handlers.onOpenStatistics ? (
        <TableActionTooltip label="Statistics">
          <button
            type="button"
            onClick={() =>
              handlers.onOpenStatistics?.({
                officeId,
                firmId,
                label: statsLabel,
              })
            }
            className={ACTION_ICON_BUTTON_CLASS}
            aria-label="Statistics"
          >
            <span className={ACTION_ICON_SIZE_CLASS} aria-hidden>
              <Stats />
            </span>
          </button>
        </TableActionTooltip>
      ) : null}
      {showFirmActions && firmId ? (
        <>
          {handlers.onOpenFirmDescription ? (
            <TableActionTooltip label="Firm Description">
              <button
                type="button"
                onClick={() =>
                  handlers.onOpenFirmDescription?.({ firmId, name: firmName })
                }
                className={ACTION_ICON_BUTTON_CLASS}
                aria-label="Firm Description"
              >
                <FirmDescriptionIcon className={ACTION_SVG_ICON_CLASS} aria-hidden />
              </button>
            </TableActionTooltip>
          ) : null}
          {handlers.onOpenFirmLogo ? (
            <TableActionTooltip label="Firm Logo">
              <button
                type="button"
                onClick={() =>
                  handlers.onOpenFirmLogo?.({ firmId, name: firmName })
                }
                className={ACTION_ICON_BUTTON_CLASS}
                aria-label="Firm Logo"
              >
                <span
                  className={`${ACTION_ICON_SIZE_CLASS} ${ACTION_SVG_PATH_FILL_CLASS}`}
                  aria-hidden
                >
                  <Image />
                </span>
              </button>
            </TableActionTooltip>
          ) : null}
        </>
      ) : null}
      {showLogButton ? (
        <TableActionTooltip label="Modifications Log">
          <button
            type="button"
            onClick={openFirmLog}
            className={ACTION_ICON_BUTTON_CLASS}
            aria-label="Modifications Log"
          >
            <span className={ACTION_ICON_SIZE_CLASS} aria-hidden>
              <Log />
            </span>
          </button>
        </TableActionTooltip>
      ) : null}
      {hasExpand ? (
        <button
          type="button"
          onClick={() => handlers.onToggleFirmExpanded?.(expandFirmId)}
          className={MANAGE_FIRM_EXPAND_JURISDICTIONS_BUTTON_CLASS}
          aria-label={
            isExpanded ? "Collapse jurisdictions" : "Expand jurisdictions"
          }
        >
          <ManageFirmJurisdictionsExpandGlyph expanded={isExpanded} />
        </button>
      ) : null}
    </div>
  );
}
